import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { handleToolError, maybeShow3DayOffer } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Combobox } from "@/components/ui/combobox";
import { TrafficLight } from "@/components/TrafficLight";
import { RouteMap } from "@/components/RouteMap";
import { Mountain, Loader2, Info, Bike, Map as MapIcon, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { TrialCounter } from "@/components/TrialCounter";
import { TrialLimitModal } from "@/components/TrialLimitModal";
import { ThreeDayOfferModal } from "@/components/ThreeDayOfferModal";
import { OneAnalysisLeft } from "@/components/OneAnalysisLeft";

export default function HillClimb() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState(params.get("vehicle") || "");
  const [form, setForm] = useState({ grade_percent: 10, rider_weight_kg: 70, passenger_cargo_kg: 0, target_speed_kmh: 15 });
  const [result, setResult] = useState(null);
  const [credit, setCredit] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/vehicles", { params: { category: "motor_listrik" } }),
      api.get("/vehicles", { params: { category: "sepeda_listrik" } }),
    ]).then(([m, b]) => setVehicles([...m.data.items, ...b.data.items])).catch(() => {});
  }, []);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async () => {
    if (!vehicleId) { toast.error("Pilih kendaraan terlebih dahulu."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/analysis/hill", { vehicle_id: vehicleId, inputs: form });
      setResult(data.result); setMeta(data.meta);
      maybeShow3DayOffer(data.credit);
      setCredit(data.credit);
      await checkAuth();
      toast.success("Hasil berhasil dibuat!");
    } catch (err) { handleToolError(err, navigate); }
    finally { setLoading(false); }
  };

  const angle = Math.min(Math.atan((form.grade_percent || 0) / 100) * (180 / Math.PI), 45);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="hill-page">
      <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-navy mb-2 flex items-center gap-3"><Mountain className="w-8 h-8 text-teal" />Cek Kekuatan Tanjakan</h1>
      <div className="flex items-center gap-3 mb-8 flex-wrap">
        <p className="text-muted-foreground">Estimasi apakah kendaraan kemungkinan kuat melewati tanjakan, berdasarkan fisika sederhana.</p>
        <TrialCounter />
      </div>
      <TrialLimitModal />
      <ThreeDayOfferModal />

      <Tabs defaultValue="manual">
        <TabsList className="rounded-full bg-secondary p-1 mb-6">
          <TabsTrigger value="manual" data-testid="hill-tab-manual" className="rounded-full px-5 data-[state=active]:bg-white data-[state=active]:text-teal"><SlidersHorizontal className="w-4 h-4 mr-2" />Input Manual</TabsTrigger>
          <TabsTrigger value="route" data-testid="hill-tab-route" className="rounded-full px-5 data-[state=active]:bg-white data-[state=active]:text-teal"><MapIcon className="w-4 h-4 mr-2" />Peta Rute</TabsTrigger>
        </TabsList>

        <TabsContent value="manual">
          <div className="grid lg:grid-cols-2 gap-8">
            <Card className="rounded-2xl p-6 space-y-5">
              <div>
                <Label>Kendaraan Listrik</Label>
                <Combobox testId="hill-vehicle-select" value={vehicleId} onChange={setVehicleId} className="mt-1"
                  placeholder="Pilih kendaraan" searchPlaceholder="Cari kendaraan…"
                  options={vehicles.map((v) => ({ value: v.vehicle_id, label: `${v.brand} ${v.model}${v.variant ? ` ${v.variant}` : ""}` }))} />
              </div>
              <div>
                <div className="flex justify-between"><Label>Kemiringan tanjakan</Label><span className="font-bold text-teal">{form.grade_percent}%</span></div>
                <Slider data-testid="hill-grade" value={[form.grade_percent]} onValueChange={([v]) => set("grade_percent", v)} min={0} max={30} step={1} className="mt-3" />
                <p className="text-xs text-muted-foreground mt-1">0% = datar · 30% = sangat curam</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-sm">Berat pengendara</Label><Input data-testid="hill-rider" type="number" value={form.rider_weight_kg} onChange={(e) => set("rider_weight_kg", Number(e.target.value))} className="rounded-xl h-11 mt-1" /></div>
                <div><Label className="text-sm">Penumpang+barang</Label><Input data-testid="hill-passenger" type="number" value={form.passenger_cargo_kg} onChange={(e) => set("passenger_cargo_kg", Number(e.target.value))} className="rounded-xl h-11 mt-1" /></div>
              </div>
              <div>
                <div className="flex justify-between"><Label>Target kecepatan menanjak</Label><span className="font-bold text-teal">{form.target_speed_kmh} km/jam</span></div>
                <Slider data-testid="hill-speed" value={[form.target_speed_kmh]} onValueChange={([v]) => set("target_speed_kmh", v)} min={5} max={40} step={1} className="mt-3" />
              </div>
              <Button data-testid="hill-submit" onClick={submit} disabled={loading} className="w-full py-6 rounded-full bg-teal hover:bg-teal-hover font-semibold">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Analisis Tanjakan"}
              </Button>
            </Card>

            <div>
              <Card className="rounded-2xl p-6 mb-4 overflow-hidden">
                <div className="relative h-40">
                  <svg viewBox="0 0 300 160" className="w-full h-full">
                    <line x1="0" y1="150" x2="300" y2="150" stroke="#E2E8F0" strokeWidth="2" />
                    <line x1="40" y1="150" x2="280" y2={150 - Math.tan(angle * Math.PI / 180) * 240} stroke="#94A3B8" strokeWidth="4" strokeLinecap="round" />
                  </svg>
                  <motion.div
                    initial={{ left: "10%", bottom: "10px" }}
                    animate={{ left: result ? "70%" : "10%", bottom: result ? `${10 + Math.tan(angle * Math.PI / 180) * 200 * 0.6}px` : "10px" }}
                    transition={{ duration: 1.2, ease: "easeOut" }}
                    className="absolute" data-testid="hill-scooter"
                    style={{ transform: `rotate(-${angle}deg)` }}
                  >
                    <div className="w-11 h-11 rounded-xl bg-teal flex items-center justify-center shadow-lg"><Bike className="w-6 h-6 text-white" /></div>
                  </motion.div>
                </div>
              </Card>

              {result ? (
                <Card className="rounded-2xl p-6" data-testid="hill-result">
                  <TrafficLight status={result.status} size="lg" className="mb-4" />
                  <p className="font-heading font-bold text-lg text-navy mb-4">{result.verdict}</p>
                  {result.status !== "grey" ? (
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <Stat l="Total beban" v={`${result.total_mass_kg} kg`} />
                      <Stat l="Rasio tenaga" v={`${result.power_ratio}×`} />
                      <Stat l="Tenaga dibutuhkan" v={`${result.power_required_w} W`} />
                      <Stat l="Tenaga tersedia" v={`${result.power_available_w} W`} />
                      <Stat l="Estimasi kecepatan maks" v={`${result.estimated_max_speed_kmh} km/jam`} />
                      <Stat l="Kemiringan" v={`${result.grade_percent}%`} />
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">{result.missing_data?.join("; ")}</p>
                  )}
                  <div className="mt-4 bg-slate-50 rounded-xl p-4 flex gap-3">
                    <Info className="w-5 h-5 text-teal flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-muted-foreground">
                      <p className="font-semibold text-navy">Asumsi</p>
                      <p>{result.assumptions.catatan}</p>
                      <p className="mt-1">Sumber: {meta?.source?.source_name} · keyakinan {result.confidence}. Hasil estimasi, bukan jaminan. {meta?.data_status === "demo" && "DATA DEMO."}</p>
                    </div>
                  </div>
                  {result.status !== "grey" && (
                    <div className="mt-4 bg-teal/5 rounded-xl p-4" data-testid="hill-why">
                      <p className="font-semibold text-navy text-sm">Kenapa hasilnya demikian?</p>
                      <p className="text-sm text-muted-foreground mt-1">Daya tersedia sekitar <strong>{result.power_ratio}×</strong> kebutuhan estimasi pada kondisi yang Anda masukkan.</p>
                      <p className="text-xs text-muted-foreground mt-2">Faktor yang dapat memengaruhi hasil: kondisi baterai, suhu, permukaan jalan, tekanan ban, dan beban aktual.</p>
                    </div>
                  )}
                  {credit?.remaining === 1 && <div className="mt-4"><OneAnalysisLeft to={`/bandingkan${vehicleId ? `?add=${vehicleId}` : ""}`} label="Bandingkan Motor Ini" /></div>}
                  <Button data-testid="hill-next-compare" variant="outline" onClick={() => navigate(`/bandingkan${vehicleId ? `?add=${vehicleId}` : ""}`)} className="rounded-full mt-4 w-full">Selanjutnya: Bandingkan Kendaraan</Button>
                </Card>
              ) : (
                <Card className="rounded-2xl p-8 text-center text-muted-foreground" data-testid="hill-empty">Pilih kendaraan dan tekan Analisis Tanjakan.</Card>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="route">
          <RouteMap />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ l, v }) {
  return <div className="bg-secondary rounded-xl p-3"><p className="text-xs text-muted-foreground">{l}</p><p className="font-bold text-navy">{v}</p></div>;
}
